import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheckBig } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { getRandomSampleModel } from '../utils/sampleModels';
import { useTracking, PAYMENT_EVENTS } from '../hooks/useTracking';

interface EhUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgradeClick?: () => boolean; // return true if we should proceed to subscription page
  limitType: 'audio' | 'call' | 'text' | 'image' | 'nsfw' | 'create' | 'video';
  characterData?: {
    id?: string;
    name?: string;
    imageUrl?: string;
  };
  // usageStats?: {
  //   textMessages: number;
  //   images: number;
  //   textLimit: number;
  //   imageLimit: number;
  // };
}

export function EhUpgradeDialog({
  open,
  onOpenChange,
  onUpgradeClick,
  limitType,
  characterData,
  // usageStats
}: EhUpgradeDialogProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(open);
  const { trackEvent } = useTracking();
  
  useEffect(() => {
    setIsOpen(open);
    if (open) {
      // Track when upgrade dialog is shown
      trackEvent(PAYMENT_EVENTS.UPGRADE_DIALOG_SHOWN, { 
        limitType,
        characterId: characterData?.id,
        characterName: characterData?.name
      });
    }
  }, [open, trackEvent, limitType, characterData]);
  
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange(newOpen);
  };
  
  const handleSubscribeClick = () => {
    // Track when user clicks upgrade button
    trackEvent(PAYMENT_EVENTS.UPGRADE_DIALOG_CLICK, {
      limitType,
      characterId: characterData?.id,
      characterName: characterData?.name
    });
    
    if (onUpgradeClick && !onUpgradeClick()) {
      // it is up to the parent to handle the case where user is not logged in.
      // Generally showing EhAuthDialog.
      return;
    }

    // Close dialog and navigate to subscription page
    handleOpenChange(false);
    navigate('/subscription');
  };

  const getLimitMessage = () => {
    // if (!usageStats) return `Upgrade for unlimited messaging with ${characterData?.name} and many more.`;
    
    if (limitType === 'audio') {
      return `Unlimited voice notes${characterData?.name ? ` from ${characterData?.name}` : ''}`;
    } else if (limitType === 'call') {
      return `Ultrarealistic phone calls${characterData?.name ? ` with ${characterData?.name}` : ''}`;
    } else if (limitType === 'image') {
      return `Unlimited images and messages${characterData?.name ? ` from ${characterData?.name}` : ''}`;
    } else if (limitType === 'nsfw') {
      return <span>
        <span>Reveal </span>
        <span className="bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">NSFW</span>
        <span>{characterData?.name ? ` content from ${characterData?.name}` : ' content' }</span>
      </span>;
    } else if (limitType === 'create') {
      return `Unlimited character creation and customization`;
    } else if (limitType === 'video') {
      return `Hot video generation${characterData?.name ? ` with ${characterData?.name}` : ''}`;
    } else {
      return `Unlimited messaging${characterData?.name ? ` with ${characterData?.name}` : ''}`;
    }
  };

  const randomSampleModel = getRandomSampleModel();

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-800 p-0 flex flex-col md:flex-row overflow-hidden gap-0">
        {/* Character image (if available) */}
        {/* characterData?.imageUrl && */ (
          <div className="w-full md:w-1/2 aspect-square md:aspect-auto flex items-center justify-center overflow-hidden">
            <img
              src={characterData?.imageUrl || randomSampleModel?.assetPath}
              alt={characterData?.name || 'Character'}
              className="object-cover w-full h-full"
            />
          </div>
        )}
        
        {/* Dialog content */}
        <div className={`flex-1 p-6 ${characterData?.imageUrl ? 'md:w-1/2' : 'w-full'}`}>
          <DialogHeader>
            <DialogTitle className="text-white text-2xl flex items-center gap-2 mb-2">
              {getLimitMessage()}
            </DialogTitle>
            <DialogDescription className="text-white text-md -mb-2">
              Subscribe to unlock:
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 mb-2 text-white/80">
            <ul className="space-y-2">
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-300">Unlimited messages with all characters</span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                {/* <span className="text-sm text-zinc-300">Unlimited text messages</span> */}
                <span className="text-sm text-zinc-300">Uncensored Images</span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                {/* <span className="text-sm text-zinc-300">Unlimited text messages</span> */}
                <span className="inline-flex items-center gap-2">
                  <span className="text-sm text-zinc-300">Uncensored Videos</span>
                  <span className="text-xs text-zinc-800 font-bold bg-amber-500 rounded-md px-1.5 py-1 select-none">NEW</span>
                </span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-300">Voice notes</span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                {/* <span className="text-sm text-zinc-300">Unlimited text messages</span> */}
                <span className="inline-flex items-center gap-2">
                  <span className="text-sm text-zinc-300">Ultrarealistic phone calls</span>
                  <span className="text-xs text-zinc-800 font-bold bg-amber-500 rounded-md px-1.5 py-1 select-none">NEW</span>
                </span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-300">Faster response time</span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-300">Reveal blurred images</span>
              </li>
              <li className="flex items-start">
                <CircleCheckBig className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-zinc-300">Create unlimited characters</span>
              </li>
            </ul>
          </div>
          
          {characterData?.name && (
            <p className="text-white font-semibold mt-4">
              {limitType === 'call' ? (
                <span >{`${characterData.name} is waiting for your call!`}</span>
              ) : (
                <span>{`${characterData.name} is waiting to continue your conversation!`}</span>
              )}
            </p>
          )}
          
          <DialogFooter className="mt-6">
            <div className="flex flex-col gap-2 w-full items-center">
              <Button
                type="button" 
                size="lg"
                onClick={handleSubscribeClick}
                className={
                  'bg-gradient-to-r from-pink-600 to-pink-800 hover:from-pink-500 hover:to-pink-700 '
                  + 'w-full text-white font-semibold py-2 transition-colors'
                }
              >
                <span>Upgrade Now</span>
              </Button>
              {['text', 'image'].includes(limitType) ? (
                <div className="text-white/30 text-xs select-none">
                  Or come back tomorrow...
                </div>
              ) : ['video'].includes(limitType) && (
                <div className="text-white/30 text-xs select-none">
                  Or wait next month for a new free video...
                </div>
              )}
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
} 